import styled from '@emotion/styled'
import React, { useState } from 'react'
import { useQuery } from '../../../api/graphql'
import { useChatContext } from './ChatProvider'
import { SUPPORT_CONVERSATIONS_QUERY } from './chatQueries'
import { formatRelativeTime } from './timeUtils'

const Container = styled.div`
  flex: 1;
  overflow-y: auto;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
  text-align: center;
`

const EmptyKite = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin-bottom: 16px;
`

const EmptyHeadline = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #102638;
`

const EmptySubtext = styled.p`
  margin: 8px 0 20px;
  font-size: 14px;
  color: #6b7280;
`

const StartButton = styled.button`
  background: #6cc14c;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #5aad3e;
  }
`

const ConversationItem = styled.button<{ hasUnread: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  border: none;
  background: ${props => (props.hasUnread ? '#f0fdf4' : 'white')};
  cursor: pointer;
  text-align: left;
  min-width: 0;
  border-bottom: 1px solid #f3f4f6;

  &:hover {
    background: #f9fafb;
  }
`

const ConversationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const Subject = styled.span<{ hasUnread: boolean }>`
  font-size: 14px;
  font-weight: ${props => (props.hasUnread ? 600 : 400)};
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  line-height: 1.4;
  padding-bottom: 1px;
`

const Time = styled.span`
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;
  margin-left: 8px;
`

const Preview = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 4px 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  line-height: 1.4;
  padding-bottom: 1px;
`

const UnreadBadge = styled.span`
  background: #6cc14c;
  color: white;
  font-size: 11px;
  font-weight: 600;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  margin-left: 8px;
`

const BottomActions = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid #f3f4f6;
`

const NewButton = styled.button`
  width: 100%;
  padding: 10px;
  background: #102638;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #1a3a52;
  }
`

const ClosedToggle = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 0;

  &:hover {
    color: #102638;
  }
`

type Conversation = {
  id: string
  subject: string
  status: string
  lastMessageAt: string
  unreadCount: number
  messages: Array<{ body: string; senderType: string }>
}

const ConversationList: React.FC = () => {
  const { openConversation, startNewConversation } = useChatContext()
  const [showClosed, setShowClosed] = useState(false)
  const { data, loading } = useQuery(SUPPORT_CONVERSATIONS_QUERY, {
    variables: { includeClosed: showClosed },
    pollInterval: 30000,
  })

  const conversations: Conversation[] = data?.supportConversations ?? []

  return (
    <>
      <Container>
        {loading && conversations.length === 0 ? (
          <EmptyState>
            <p style={{ color: '#6b7280', fontSize: 14 }}>Loading...</p>
          </EmptyState>
        ) : conversations.length === 0 && !showClosed ? (
          <EmptyState>
            <EmptyKite src="/img/email-kite-logo.png" alt="" />
            <EmptyHeadline>Hi! How can we help?</EmptyHeadline>
            <EmptySubtext>Start a conversation and we&#39;ll get back to you shortly.</EmptySubtext>
            <StartButton onClick={startNewConversation}>Start a conversation</StartButton>
          </EmptyState>
        ) : conversations.length === 0 && showClosed ? (
          <EmptyState>
            <EmptySubtext>No closed conversations.</EmptySubtext>
          </EmptyState>
        ) : (
          conversations.map(conversation => {
            const lastMessage = conversation.messages?.[conversation.messages.length - 1]
            const hasUnread = conversation.unreadCount > 0

            return (
              <ConversationItem key={conversation.id} hasUnread={hasUnread} onClick={() => openConversation(conversation.id)}>
                <ConversationHeader>
                  <Subject hasUnread={hasUnread}>{conversation.subject}</Subject>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Time>{formatRelativeTime(conversation.lastMessageAt)}</Time>
                    {hasUnread && <UnreadBadge>{conversation.unreadCount}</UnreadBadge>}
                  </div>
                </ConversationHeader>
                {lastMessage && <Preview>{lastMessage.body}</Preview>}
              </ConversationItem>
            )
          })
        )}
      </Container>
      <BottomActions>
        <NewButton onClick={startNewConversation}>New Conversation</NewButton>
        <ClosedToggle onClick={() => setShowClosed(prev => !prev)}>
          {showClosed ? 'Hide closed' : 'View closed'}
        </ClosedToggle>
      </BottomActions>
    </>
  )
}

export default ConversationList
